<script setup lang="ts">
import ImageUploaderNew from '@/components/ui/ImageUploader/ImageUploaderNew.vue'
import type AppFormInput from '@/components/ui/AppFormInput.vue'
import { useVModel } from '@vueuse/core'

import RewardFormBlock from '@/views/reward/components/RewardFormBlock.vue'

import type { ImageFile, LoyaltyReward } from '@/types/types'

const props = defineProps<{
  editingReward: LoyaltyReward
}>()

const emit = defineEmits<{
  (e: 'update:editingReward', value: LoyaltyReward): void
  (e: 'update:nameChanged', name: string): void
}>()

const editingReward = useVModel(props, 'editingReward', emit)

const { t } = useI18n()

const REWARD_NAME_MAX_LENGTH = 50
const REWARD_DESCRIPTION_MAX_LENGTH = 145

const rewardDescription = computed({
  get: () => editingReward.value.description ?? '',
  set: (value: string) => {
    editingReward.value.description = value
  }
})

const errors = ref<string[]>([])
const nameContainer = ref<HTMLElement | null>(null)
const nameInputRef = ref<InstanceType<typeof AppFormInput> | null>(null)

const imageUploader = ref<InstanceType<typeof ImageUploaderNew> | null>(null)
const initialImages = ref<ImageFile[]>([])
const images = ref<ImageFile[]>([])
const deletedImages = ref<number[]>([])

const resetErrors = () => {
  errors.value = []
}

const updateImages = (newImages: ImageFile[]) => {
  images.value = newImages

  if (newImages.length > 0) {
    editingReward.value.pictureUrl = newImages[0].path
  } else {
    editingReward.value.pictureUrl = '/images/default-profile-picture.jpg'
  }
}

const updateDeletedImages = (deletedIds: number[]) => {
  deletedImages.value = deletedIds
}

const resetImage = () => {
  if (editingReward.value.pictureUrl) {
    initialImages.value = [
      {
        id: Date.now(),
        path: editingReward.value.pictureUrl,
        name: 'reward-image'
      }
    ]
  } else {
    initialImages.value = []
  }

  images.value = []
  deletedImages.value = []
}

const setNameError = () => {
  errors.value = [t('pages.loyaltyPage.edit.fields.nameError')]
  nameInputRef.value?.focus()
}

const handleNameChange = (value: string) => {
  resetErrors()
  emit('update:nameChanged', value)
}

onMounted(() => {
  resetImage()
})

defineExpose({
  setNameError
})
</script>

<template>
  <RewardFormBlock :title="t('pages.offer.reward.identitySectionTitle')">
    <div ref="nameContainer">
      <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
        <span>{{ t('pages.reward.details.nameLabel') }}</span>
        <span class="text-red-500">*</span>
      </label>
      <AppFormInput
        id="reward-name-offer"
        ref="nameInputRef"
        v-model="editingReward.name"
        class="w-full"
        :maxlength="REWARD_NAME_MAX_LENGTH"
        :errors="errors"
        error-classes="!mt-1 !text-xs"
        @update:model-value="handleNameChange"
      />
      <p v-if="errors.length === 0" class="text-xs text-muted-foreground mt-1">
        {{ editingReward.name.length }}/{{ REWARD_NAME_MAX_LENGTH }} {{ t('pages.reward.details.characters') }}
      </p>
    </div>

    <div>
      <label for="reward-description-offer" class="mb-2 flex items-center gap-1 text-sm text-foreground">
        <span>{{ t('pages.reward.details.descriptionLabel') }}</span>
        <Badge variant="secondary">{{ t('pages.reward.details.descriptionWebappOnly') }}</Badge>
      </label>
      <Textarea
        id="reward-description-offer"
        v-model="rewardDescription"
        :placeholder="t('pages.reward.details.descriptionPlaceholder')"
        :rows="3"
        :maxlength="REWARD_DESCRIPTION_MAX_LENGTH"
        class="w-full text-sm"
      />
      <p class="text-xs text-muted-foreground mt-1">
        {{ rewardDescription.length }}/{{ REWARD_DESCRIPTION_MAX_LENGTH }} {{ t('pages.reward.details.characters') }}
      </p>
    </div>

    <div>
      <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
        <span>{{ t('pages.reward.details.imageLabel') }}</span>
        <span class="text-red-500">*</span>
      </label>

      <ImageUploaderNew
        ref="imageUploader"
        :images="images"
        :initial-images="initialImages"
        :deleted-images="deletedImages"
        :single-image="true"
        :aspect-ratio="[1, 1]"
        @update:images="updateImages"
        @update:deleted-images="updateDeletedImages"
      />
    </div>
  </RewardFormBlock>
</template>
